/**
 * Admin System Storage API Service
 *
 * Provides access to disk usage and backend log file management endpoints.
 */

import { apiClient } from "./apiClient";
import { getApiBase } from "./apiConfig";

export interface StorageOverview {
  disk_total_bytes: number;
  disk_used_bytes: number;
  disk_free_bytes: number;
  disk_percent_used: number;
  logs_dir_path: string;
  logs_dir_total_bytes: number;
  logs_dir_file_count: number;
  timestamp: number;
}

export interface LogFileItem {
  name: string;
  size_bytes: number;
  size_human: string;
  modified_at: string;
  is_active: boolean;
}

export interface LogsListResponse {
  files: LogFileItem[];
  total_bytes: number;
  count: number;
  timestamp: number;
}

export interface DeleteResultResponse {
  status: string;
  message: string;
  deleted_count: number;
  freed_bytes: number;
}

/**
 * Get storage overview (disk usage + logs directory aggregate).
 */
export async function getStorageOverview(): Promise<StorageOverview> {
  try {
    const response = await apiClient.get<StorageOverview>("/admin/storage/overview", {
      timeout: 15000,
    });
    return response.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.detail || error?.message || "Failed to fetch storage overview";
    throw new Error(message);
  }
}

/**
 * List backend log files.
 */
export async function getStorageLogs(): Promise<LogsListResponse> {
  try {
    const response = await apiClient.get<LogsListResponse>("/admin/storage/logs", {
      timeout: 15000,
    });
    return response.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.detail || error?.message || "Failed to fetch log files";
    throw new Error(message);
  }
}

/**
 * Delete a single log file by name.
 */
export async function deleteLogFile(filename: string): Promise<DeleteResultResponse> {
  try {
    const response = await apiClient.delete<DeleteResultResponse>(
      `/admin/storage/logs/${encodeURIComponent(filename)}`
    );
    return response.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.detail || error?.message || "Failed to delete log file";
    throw new Error(message);
  }
}

/**
 * Delete all non-active log files older than the given day threshold.
 */
export async function deleteLogFilesOlderThan(
  olderThanDays: number
): Promise<DeleteResultResponse> {
  try {
    const response = await apiClient.post<DeleteResultResponse>(
      "/admin/storage/logs/delete-older",
      { older_than_days: olderThanDays }
    );
    return response.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.detail || error?.message || "Failed to delete old log files";
    throw new Error(message);
  }
}

/**
 * Build a direct browser download URL for a log file.
 *
 * Uses a plain same-origin URL so the HttpOnly session cookie is sent
 * automatically and the browser handles the attachment download.
 */
export function getLogFileDownloadUrl(filename: string): string {
  return `${getApiBase()}/api/admin/storage/logs/${encodeURIComponent(filename)}/download`;
}
